// Venue local time, which is the only clock a schedule is written in.
//
// "An hour from noon" is noon where the stand is. The roadmap is explicit that a
// screen must never decide for itself what noon means, and there are two
// separate ways to get that wrong: reading the browser's own timezone, which is
// whatever the machine was set up with in an office in another country, and
// converting the venue's wall clock to an instant in UTC when the schedule is
// written, which quietly bakes in the offset that was in force on the day
// somebody typed it.
//
// So no conversion happens in either direction. A bound is a wall clock reading
// with no offset on it, the current instant is turned into the venue's wall
// clock reading, and the two are compared as text. That is what makes daylight
// saving a non-event on the ordinary path: noon is noon on both sides of a
// changeover because neither side ever held an offset to be wrong about.
//
// What it does not fix is the two hours a year where a wall clock is not a
// function of time. On the autumn changeover the venue's clock reads 01:30
// twice, so a window over that hour opens twice; on the spring one it never
// reads 02:30, so a window inside the lost hour never opens. Both are named in
// the README and in check.mjs rather than papered over, because the honest
// answer for an event that runs 09:00 to 18:00 is that neither can happen, and
// the dishonest one would be a heuristic that guesses which 01:30 was meant.

/** A wall clock reading with no offset on it: `YYYY-MM-DDTHH:MM:SS`. */
export type LocalStamp = string;

/**
 * A window bound, in one of two shapes.
 *
 * `dated` is one moment in the venue's calendar, which is what a console writes
 * for a slot on a particular day. `daily` is a time of day that comes round
 * every day the screen is switched on, which is what a person means by "the
 * sponsor loop runs over lunch" across a three-day show. Both are wall clock
 * readings and neither carries an offset.
 */
export type Bound =
  | { readonly shape: "dated"; readonly at: LocalStamp }
  | { readonly shape: "daily"; readonly at: string };

// One venue per screen, so this holds one entry in practice. It is capped
// anyway, because an unbounded cache on a surface that runs for twelve hours is
// exactly the kind of slow growth this package is meant to be careful about,
// and because a check that drives many timezones would otherwise leave a
// formatter behind for each one.
const FORMATTER_CAP = 8;
const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  const held = formatters.get(timeZone);
  if (held !== undefined) return held;

  const made = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  if (formatters.size >= FORMATTER_CAP) formatters.clear();
  formatters.set(timeZone, made);
  return made;
}

/** How many formatters are being held. Exported so a check can watch it stay small. */
export const formattersHeld = (): number => formatters.size;

/**
 * Whether this machine knows the zone by name.
 *
 * A schedule naming a zone the browser has never heard of is refused rather
 * than fallen back from, because every fallback available here is a different
 * timezone and a screen running an hour out is worse than a screen that says
 * the schedule was refused and keeps showing what it had.
 */
export function isKnownTimeZone(name: string): boolean {
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: name });
    return true;
  } catch {
    return false;
  }
}

/** The venue's wall clock at an instant, as `YYYY-MM-DDTHH:MM:SS`. */
export function venueLocal(timeZone: string, atMs: number): LocalStamp {
  const parts = formatterFor(timeZone).formatToParts(new Date(atMs));
  const held: Record<string, string> = {};
  for (const part of parts) held[part.type] = part.value;

  // Intl gives midnight as "24" under some ICU versions with hourCycle h23 on
  // some locales, and a stamp reading 24:00:00 sorts after every other reading
  // of that day rather than before them, which would hold a window open for a
  // day too long.
  const hour = held["hour"] === "24" ? "00" : (held["hour"] ?? "00");

  return `${held["year"] ?? "0000"}-${held["month"] ?? "01"}-${held["day"] ?? "01"}T${hour}:${held["minute"] ?? "00"}:${held["second"] ?? "00"}`;
}

/** The time of day out of a stamp, `HH:MM:SS`. */
export const timeOfDay = (stamp: LocalStamp): string => stamp.slice(11);

const DATED = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;
const DAILY = /^(\d{2}):(\d{2})(?::(\d{2}))?$/;

/**
 * A bound as written by a console, or null if it is not one.
 *
 * Seconds are optional on the way in and always present on the way out, because
 * every comparison below is a string comparison and `12:00` sorts after
 * `12:00:00` rather than equal to it.
 */
export function readBound(raw: unknown): Bound | null {
  if (typeof raw !== "string") return null;
  const text = raw.trim();

  const dated = DATED.exec(text);
  if (dated !== null) {
    const [, year, month, day, hour, minute, second] = dated;
    if (!realDate(Number(year), Number(month), Number(day))) return null;
    if (!realTime(Number(hour), Number(minute), Number(second ?? "0"))) return null;
    return { shape: "dated", at: `${year}-${month}-${day}T${hour}:${minute}:${second ?? "00"}` };
  }

  const daily = DAILY.exec(text);
  if (daily !== null) {
    const [, hour, minute, second] = daily;
    if (!realTime(Number(hour), Number(minute), Number(second ?? "0"))) return null;
    return { shape: "daily", at: `${hour}:${minute}:${second ?? "00"}` };
  }

  return null;
}

// Written out rather than handed to Date, because Date accepts 31 February and
// rolls it into March, and a window that silently moves to another day is the
// failure this whole module exists to prevent.
function realDate(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1) return false;
  const lengths = [31, leap(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return day <= (lengths[month - 1] ?? 0);
}

const leap = (year: number): boolean => (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

const realTime = (hour: number, minute: number, second: number): boolean =>
  hour <= 23 && minute <= 59 && second <= 59;

/**
 * Whether a bound has come round yet at a given wall clock reading.
 *
 * A dated bound is a plain comparison of two stamps. A daily one compares only
 * the time of day, so it means the same thing on every day of a show.
 */
export function reached(bound: Bound, local: LocalStamp): boolean {
  return bound.shape === "dated" ? local >= bound.at : timeOfDay(local) >= bound.at;
}

/**
 * Whether a window is open at a wall clock reading.
 *
 * Half open: an item joins the rotation at its start and has left by its stop,
 * so a stop of 13:00 and a start of 13:00 on the next item hand over without an
 * overlap and without a second where neither is eligible.
 *
 * A daily window whose stop is before its start wraps midnight, which is the
 * only sane reading of "from 22:00 until 02:00" and is a real case: a stand in
 * a hall that runs an evening reception. A dated window that ends before it
 * begins is a mistake rather than a wrap, and is refused when the schedule is
 * read rather than reinterpreted here.
 */
export function windowOpen(from: Bound | null, until: Bound | null, local: LocalStamp): boolean {
  const wraps =
    from !== null && until !== null && from.shape === "daily" && until.shape === "daily" && until.at < from.at;

  if (wraps) return reached(from, local) || !reached(until, local);

  if (from !== null && !reached(from, local)) return false;
  if (until !== null && reached(until, local)) return false;
  return true;
}
